import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import {
  AlertOctagon, CheckCircle2, Zap, Lock, MessageSquare, Loader2,
  RefreshCcw, ArrowRight, ShieldAlert,
} from 'lucide-react';

const isoTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('en-SG', { hour: '2-digit', minute: '2-digit', hour12: false });
  } catch { return ''; }
};

const friendlyDate = (ymd) => {
  if (!ymd) return '';
  try {
    const d = new Date(`${ymd}T00:00:00`);
    return d.toLocaleDateString('en-SG', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return ymd; }
};

const AUDIT_TONE = (action) => {
  if (action?.startsWith('RELIEF_AUTO') || action?.startsWith('RELIEF_CONFIRMED') || action?.startsWith('RELIEF_ASSIGNED')) return 'emerald';
  if (action?.startsWith('ABSENCE')) return 'amber';
  if (action?.startsWith('BRIEFING')) return 'slate';
  return 'slate';
};

const AUDIT_LABEL = (action) => {
  const map = {
    RELIEF_AUTO_ASSIGNED: 'Auto-assignment run',
    RELIEF_CONFIRMED: 'Relief assigned',
    RELIEF_ASSIGNED: 'Relief assigned',
    ABSENCE_CREATED: 'Absence logged',
    ABSENCE_UPDATED: 'Absence updated',
    BRIEFING_SENT: 'Daily briefing sent',
  };
  return map[action] || (action || '').replace(/_/g, ' ').toLowerCase();
};

const ReliefTodayPage = () => {
  const { user, token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryDate = searchParams.get('date');
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [focus, setFocus] = useState({ absenceId: null, period: null });
  const [assigning, setAssigning] = useState(false);

  // Gate access: admin / exco / hod only.
  useEffect(() => {
    if (user && !['admin', 'exco', 'hod'].includes(user.role)) {
      toast.error('Relief Today is available to Admin, EXCO and HOD only.');
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  const load = async (overrideFocus = null) => {
    try {
      const f = overrideFocus || focus;
      const params = new URLSearchParams();
      if (queryDate) params.set('date', queryDate);
      if (f.absenceId) params.set('focus_absence_id', f.absenceId);
      if (f.period) params.set('focus_period', String(f.period));
      const url = `${API}/relief/today/overview${params.toString() ? `?${params}` : ''}`;
      const res = await axios.get(url, authHeaders);
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load Relief Today.');
    } finally {
      setLoading(false);
    }
  };

  // Reload when ?date= changes
  useEffect(() => {
    load();
  }, [queryDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refetch when focus changes
  useEffect(() => {
    if (focus.absenceId || focus.period) {
      load(focus);
    }
  }, [focus.absenceId, focus.period]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAssignBest = async () => {
    if (!data?.focus || !data.candidates?.length) return;
    const candidate = data.candidates[0];
    setAssigning(true);
    try {
      await axios.post(
        `${API}/relief`,
        {
          absence_id: data.focus.absence_id,
          relief_teacher_id: candidate.teacher_id,
          date: data.date,
          period: data.focus.period,
          class_name: data.focus.class_name,
          subject: data.focus.subject,
        },
        authHeaders
      );
      toast.success(`Assigned ${candidate.name} → ${data.focus.class_name} ${data.focus.period}`);
      setFocus({ absenceId: null, period: null });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign.');
    } finally {
      setAssigning(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="relief-today-loading">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  // Empty-state: no absences today
  if (data && data.absences.length === 0) {
    return (
      <div className="p-6 sm:p-8" data-testid="relief-today-empty">
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center max-w-2xl mx-auto">
          <div className="h-14 w-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5">
            <CheckCircle2 className="h-7 w-7 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold text-[#0F1B36] mb-2">No absences today</h2>
          <p className="text-slate-500 mb-6">No relief coverage needed for {friendlyDate(data.date)}.</p>
          <Button variant="outline" onClick={load} data-testid="relief-today-refresh-empty">
            <RefreshCcw className="h-4 w-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>
    );
  }

  const focusedAbsence = data?.absences?.find((a) => a.id === data?.focus?.absence_id);

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-slate-50 min-h-full" data-testid="relief-today-page">
      {/* Browser-chrome style header bar (decorative, matches design spec) */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="bg-slate-900 rounded-t-xl px-4 py-2.5 flex items-center gap-3 shadow-sm">
          <div className="flex gap-1.5">
            <div className="h-2.5 w-2.5 rounded-full bg-red-400/80"></div>
            <div className="h-2.5 w-2.5 rounded-full bg-amber-400/80"></div>
            <div className="h-2.5 w-2.5 rounded-full bg-emerald-400/80"></div>
          </div>
          <div className="flex-1 text-center">
            <span className="inline-flex items-center gap-1.5 bg-slate-800 text-slate-300 text-[11px] font-mono px-3 py-1 rounded">
              <Lock className="h-3 w-3" /> app.schoolzest.com / relief / today
            </span>
          </div>
          <div className="text-[11px] font-bold text-emerald-400 flex items-center gap-1.5">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
            </span>
            {friendlyDate(data?.date)} · {isoTime(data?.school_now)}
          </div>
        </div>

        {/* 2-column grid: Main (8) + Right rail (4) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 bg-white rounded-b-xl border-x border-b border-slate-200 shadow-sm overflow-hidden">
          {/* ─── Main Panel ─── */}
          <main className="lg:col-span-8 p-5 sm:p-7 border-r border-slate-100" data-testid="relief-today-main">
            <div className="flex items-start justify-between mb-5 gap-3">
              <div>
                <h1 className="text-2xl sm:text-3xl font-black text-[#0F1B36] font-['Outfit',sans-serif] leading-tight">
                  Relief Assignment <span className="text-slate-400 font-normal text-xl">— {friendlyDate(data?.date).split(',')[0]}</span>
                </h1>
                <p className="text-sm text-slate-500 mt-1">
                  {friendlyDate(data?.date)} · <span className="font-semibold text-slate-700">{data?.total_unassigned}</span> period{data?.total_unassigned === 1 ? '' : 's'} require coverage
                </p>
              </div>
              {data?.total_unassigned > 0 && (
                <span className="inline-flex items-center gap-1.5 text-xs font-bold text-amber-700 bg-amber-100 border border-amber-200 px-3 py-1.5 rounded-full shrink-0" data-testid="relief-today-unassigned-badge">
                  <Zap className="h-3.5 w-3.5" /> {data.total_unassigned} Unassigned
                </span>
              )}
            </div>

            {/* Loop through absences */}
            {data?.absences.map((absence) => {
              const isFocusAbs = absence.id === data?.focus?.absence_id;
              return (
                <div key={absence.id} className="mb-7 last:mb-0" data-testid={`absence-block-${absence.id}`}>
                  {/* Absence card */}
                  <div className="bg-rose-50/70 border border-rose-100 rounded-xl p-4 flex items-center gap-3 mb-4">
                    <div className="h-10 w-10 rounded-full bg-rose-100 flex items-center justify-center shrink-0">
                      <AlertOctagon className="h-5 w-5 text-rose-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm sm:text-base font-bold text-[#0F1B36] truncate">
                        {absence.teacher_name} — Absent today
                      </p>
                      <p className="text-[11px] sm:text-xs text-slate-500 truncate">
                        {absence.job_title ? `${absence.job_title} · ` : ''}
                        {absence.slots.length} period{absence.slots.length === 1 ? '' : 's'} affected
                      </p>
                    </div>
                    <span className="text-[10px] font-bold text-slate-400 font-mono shrink-0">
                      {isoTime(absence.created_at)}
                    </span>
                  </div>

                  {/* Affected periods grid */}
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">Affected Periods</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                    {absence.slots.map((slot) => {
                      const slotKey = `${absence.id}::${slot.period}`;
                      const isFocused = isFocusAbs && String(data?.focus?.period) === String(slot.period);
                      const tone = slot.assigned
                        ? 'bg-emerald-50 border-emerald-200'
                        : isFocused
                          ? 'bg-amber-100/70 border-amber-400 ring-2 ring-amber-300'
                          : 'bg-amber-50/60 border-amber-200 hover:border-amber-400';
                      return (
                        <button
                          key={slotKey}
                          onClick={() => !slot.assigned && setFocus({ absenceId: absence.id, period: slot.period })}
                          disabled={slot.assigned}
                          className={`text-left rounded-lg p-3 border transition-all ${tone} ${slot.assigned ? 'cursor-default' : 'cursor-pointer'}`}
                          data-testid={`affected-slot-${slotKey}`}
                        >
                          <p className="text-[11px] font-bold text-[#0F1B36] leading-tight">
                            {slot.period}{slot.time_slot ? ` · ${slot.time_slot.split('-')[0]}` : ''}
                          </p>
                          <p className="text-[11px] text-slate-700 mt-0.5 truncate">{slot.class_name}</p>
                          <p className="text-[10px] text-slate-500 mt-0.5 truncate">{slot.subject}</p>
                          <p className={`text-[10px] font-bold mt-1.5 flex items-center gap-1 ${slot.assigned ? 'text-emerald-700' : 'text-amber-700'}`}>
                            {slot.assigned && <CheckCircle2 className="h-2.5 w-2.5" />}
                            {slot.assigned ? 'Assigned' : 'Unassigned'}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {/* Auto-assignment ranking — for focused slot */}
            {data?.focus && data?.candidates?.length > 0 && (
              <div className="mt-6 pt-6 border-t border-slate-100" data-testid="relief-today-engine-block">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <p className="text-sm font-bold text-[#0F1B36]">
                    Auto-Assignment · {data.focus.period} <ArrowRight className="inline h-3.5 w-3.5 text-slate-400 mx-1" /> {data.focus.class_name}
                  </p>
                  <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-100 px-2 py-1 rounded-full">
                    Smart Ranked
                  </span>
                </div>

                <div className="space-y-2 mb-5">
                  {data.candidates.map((c, idx) => {
                    const best = idx === 0;
                    return (
                      <div
                        key={c.teacher_id}
                        className={`relative rounded-xl border p-4 flex items-center gap-4 ${best ? 'bg-emerald-50 border-emerald-400 shadow-sm' : 'bg-white border-slate-200'}`}
                        data-testid={`candidate-${idx}`}
                      >
                        {best && (
                          <span className="absolute -top-2 left-4 text-[9px] font-black tracking-widest text-emerald-700 bg-white border border-emerald-300 px-2 py-0.5 rounded">
                            BEST MATCH
                          </span>
                        )}
                        <div className={`h-11 w-11 rounded-full ${best ? 'bg-emerald-600' : 'bg-slate-600'} text-white text-sm font-bold flex items-center justify-center shrink-0`}>
                          {c.initials}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-base font-bold text-[#0F1B36] truncate">{c.name}</p>
                          <div className="flex flex-wrap gap-1.5 mt-1">
                            {c.free_period && <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-100 text-emerald-700">Free {data.focus.period}</span>}
                            {c.subject_match && <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-indigo-100 text-indigo-700">{data.focus.subject}</span>}
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-slate-100 text-slate-600">
                              {c.weekly_workload}/{c.max_weekly_workload} duties
                            </span>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <p className={`text-2xl font-black leading-none ${best ? 'text-emerald-600' : 'text-slate-400'}`}>{c.score}</p>
                          <p className="text-[9px] text-slate-400 uppercase tracking-wider mt-0.5">Score</p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Assign / Override row */}
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    onClick={handleAssignBest}
                    disabled={assigning}
                    className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white font-bold h-12 rounded-lg flex items-center justify-center gap-2"
                    data-testid="relief-today-assign-best"
                  >
                    {assigning ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                    Assign {data.candidates[0].name.split(' ').slice(0, 2).join(' ')} — Notify via Email
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/relief')}
                    className="border-slate-300 text-slate-700 hover:bg-slate-50 font-bold h-12 px-6 rounded-lg"
                    data-testid="relief-today-override"
                  >
                    Override
                  </Button>
                </div>
              </div>
            )}

            {data?.focus && data?.candidates?.length === 0 && (
              <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-5 flex items-start gap-3" data-testid="relief-today-no-candidates">
                <ShieldAlert className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-bold text-amber-900">No eligible relief teachers</p>
                  <p className="text-xs text-amber-700 mt-1">
                    Everyone is either at workload capacity, has a clashing class, or has opted out for this period. Override manually from the Relief Duties page.
                  </p>
                </div>
              </div>
            )}
          </main>

          {/* ─── Right Rail ─── */}
          <aside className="lg:col-span-4 p-5 sm:p-6 bg-white" data-testid="relief-today-rail">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-3">Relief Workload · This Week</p>
            <div className="space-y-3 mb-6">
              {data?.workload_bars?.length ? data.workload_bars.map((w) => {
                const tone = w.pct >= 80 ? 'bg-rose-500' : w.pct >= 50 ? 'bg-amber-500' : 'bg-emerald-500';
                return (
                  <div key={w.teacher_id} data-testid={`workload-${w.teacher_id}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-bold text-slate-700 truncate pr-2">{w.name}</span>
                      <span className="text-[11px] font-bold text-slate-500 font-mono shrink-0">{w.weekly}/{w.max}</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full ${tone} rounded-full transition-all`} style={{ width: `${w.pct}%` }}></div>
                    </div>
                  </div>
                );
              }) : (
                <p className="text-xs text-slate-400">No relief teachers yet.</p>
              )}
            </div>

            <hr className="border-slate-100 mb-5" />

            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-3">Audit Log · Today</p>
            {data?.audit?.length ? (
              <div className="space-y-3 mb-5">
                {data.audit.map((a, i) => {
                  const tone = AUDIT_TONE(a.action);
                  const dotCls = tone === 'emerald' ? 'bg-emerald-500' : tone === 'amber' ? 'bg-amber-500' : 'bg-slate-400';
                  return (
                    <div key={i} className="flex items-start gap-2.5" data-testid={`audit-row-${i}`}>
                      <div className={`h-1.5 w-1.5 rounded-full mt-1.5 shrink-0 ${dotCls}`}></div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-slate-700 leading-snug">
                          <strong className="capitalize">{AUDIT_LABEL(a.action)}</strong>
                          {a.actor_name && a.actor_name !== 'System' ? ` · ${a.actor_name}` : ''}
                        </p>
                        <p className="text-[10px] text-slate-400 font-mono mt-0.5">{isoTime(a.timestamp)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-slate-400 mb-5">No events logged today.</p>
            )}

            {/* Notification card (last briefing or sample) */}
            {focusedAbsence && (
              <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3" data-testid="relief-today-notif-card">
                <div className="flex items-center gap-2 mb-2">
                  <div className="h-6 w-6 rounded bg-emerald-500 flex items-center justify-center shrink-0">
                    <MessageSquare className="h-3.5 w-3.5 text-white" />
                  </div>
                  <span className="text-[11px] font-bold text-emerald-700 flex-1">Notification preview</span>
                  <span className="text-[10px] text-emerald-600 font-mono">{isoTime(data?.school_now)}</span>
                </div>
                <p className="text-[11px] text-emerald-900 leading-snug">
                  &ldquo;Hi <strong>{data?.candidates?.[0]?.name?.split(' ').slice(0, 2).join(' ') || 'Teacher'}</strong>, you&apos;re being considered for relief at <strong>{data?.focus?.class_name} · {data?.focus?.period}</strong>. Confirm to send via email + WhatsApp.&rdquo;
                </p>
              </div>
            )}

            <div className="mt-5 pt-4 border-t border-slate-100">
              <Button variant="outline" onClick={load} className="w-full border-slate-300 text-slate-700 hover:bg-slate-50" data-testid="relief-today-refresh">
                <RefreshCcw className="h-3.5 w-3.5 mr-2" /> Refresh
              </Button>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default ReliefTodayPage;
